import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Award, BookOpen, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { setPageMeta } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

const QUICK_CITIES = [
  "Milano", "Roma", "Napoli", "Torino", "Bologna", "Firenze",
  "Genova", "Bari", "Palermo", "Verona", "Padova", "Venezia",
];

// Esempi di esperienze rimossi su richiesta: la landing ora salta direttamente
// dalla preview del form al cross-sell dei prodotti.

export default function RaccontaLanding() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [schoolsTotal, setSchoolsTotal] = useState("21.000");

  useEffect(() => {
    setPageMeta({
      title:
        "Non basta un Open day — Racconta la tua esperienza in 2 minuti | Per manina",
      description:
        "Bastano qualche emoji, una stella e una frase per aiutare gli altri genitori del tuo quartiere a scegliere la scuola d'infanzia giusta.",
      canonicalPath: "/racconta",
      ogType: "website",
    });
    trackEvent("landing_view", { landing: "racconta-preview" });
    api
      .get("/schools", { params: { limit: 1 } })
      .then(({ data }) => {
        if (data?.total) setSchoolsTotal(data.total.toLocaleString("it-IT"));
      })
      .catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault();
    const q = query.trim();
    trackEvent("landing_search_submit", { landing: "racconta-preview", query_len: q.length });
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    navigate(`/cerca?${params.toString()}`);
  };

  return (
    <div className="bg-[#FAF9F6]" data-testid="racconta-landing-page">
      {/* ============================= HERO (identico a /recensisci) ============================= */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/ads/ads-2-mother-observing.png"
            alt="Una mamma osserva i bambini entrare a scuola"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A362D]/70 via-[#1A362D]/55 to-[#1A362D]/85" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-40 sm:py-28 lg:py-32">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-[#F2C94C] tracking-wider">
              La community indipendente dei genitori italiani
            </p>
            <h1
              className="mt-4 font-display font-semibold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight fade-up"
              data-testid="racconta-hero-title"
            >
              Per aiutare un genitore,<br />non basta un Open day.
              <br />
              <span className="text-[#F2C94C]">Ma a te bastano due minuti.</span>
            </h1>
            <p className="mt-6 font-ui text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl fade-up delay-1">
              Nessun questionario lungo da compilare. Solo <strong>qualche emoji</strong>, <strong>una stella</strong> e{" "}
              <strong>una frase</strong>: quella che riassume meglio il rapporto con le educatrici,
              i servizi della scuola o le attività. Insomma, il punto di vista utile che ad un meeting
              con la coordinatrice non si potrebbe avere!{" "}
              <span className="text-[#F2C94C] font-semibold">Racconta la tua esperienza ora!</span>
            </p>

            <form
              onSubmit={submitSearch}
              className="mt-10 bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)] p-3 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 fade-up delay-2"
              data-testid="racconta-search-form"
            >
              <div className="flex-1 flex items-center bg-[#FAF9F6] border border-[#E8E6E1] rounded-xl px-4 py-3">
                <Search size={20} className="text-[#8A9691] mr-2 shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nome della scuola, città o CAP…"
                  data-testid="racconta-search-input"
                  className="flex-1 bg-transparent border-0 outline-none font-ui text-base text-[#1A362D] placeholder:text-[#8A9691]"
                  autoComplete="off"
                />
              </div>
              <button
                type="submit"
                data-testid="racconta-search-submit"
                className="btn-primary text-base px-6 py-3 whitespace-nowrap"
              >
                Trova la tua scuola
              </button>
            </form>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="font-ui text-xs uppercase tracking-wider text-white/70 mr-1">Città:</span>
              {QUICK_CITIES.map((c) => (
                <button
                  key={c}
                  onClick={() => {
                    trackEvent("landing_city_click", { landing: "racconta-preview", city: c });
                    navigate(`/migliori/${c.toLowerCase()}`);
                  }}
                  data-testid={`racconta-city-${c.toLowerCase()}`}
                  className="px-3 py-1.5 rounded-full border border-white/40 text-white font-ui text-xs hover:bg-white hover:text-[#1A362D] transition-colors"
                >
                  {c}
                </button>
              ))}
            </div>

            <p className="mt-6 font-ui text-sm text-white/80">
              Oltre <strong>{schoolsTotal}</strong> scuole dell'infanzia italiane già mappate,
              in attesa della voce dei genitori.
            </p>
          </div>
        </div>
      </section>

      {/* ============================= PREVIEW DEL FORM (fold 2) ============================= */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-3xl mx-auto">
          <p className="label-eyebrow text-[#C05C45] text-center">Ecco cosa ti chiederemo</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-[#1A362D] tracking-tight leading-tight text-center">
            Un'anteprima della scheda.<br />Semplice, veloce, guidata.
          </h2>
          <p className="mt-4 font-ui text-base text-[#5C6B64] leading-relaxed text-center max-w-xl mx-auto">
            Non un modulo. Non un questionario da compilare a puntate.
            <br className="hidden sm:block" />
            Solo domande a cui rispondere con un dito.
          </p>

          {/* --- Preview statica del form --- */}
          <div
            className="mt-10 relative rounded-2xl border border-[#E8E6E1] bg-white shadow-[0_20px_50px_-20px_rgba(26,54,45,0.18)] overflow-hidden"
            data-testid="racconta-form-preview"
            aria-hidden="true"
          >
            {/* Watermark "ANTEPRIMA" */}
            <span
              className="absolute top-4 right-4 z-10 rounded-full bg-[#F2C94C] text-[#1A362D] font-ui text-[10px] tracking-wider px-2.5 py-1 font-medium uppercase"
              data-testid="racconta-preview-badge"
            >
              Anteprima
            </span>

            <div className="p-6 sm:p-8 space-y-6 pointer-events-none select-none">
              {/* Domanda 1 — stelle */}
              <div>
                <p className="font-ui text-[15px] text-[#1A362D] font-medium">
                  Quanto consiglieresti questa scuola a un genitore?
                </p>
                <div className="mt-2 text-3xl">★ ★ ★ ★ <span className="text-[#E8E6E1]">★</span></div>
              </div>

              {/* Domanda 2 — 3 righe emoji come esempio */}
              <div className="pt-2 border-t border-[#E8E6E1] space-y-3">
                <p className="font-ui text-xs text-[#8A9691]">
                  Puoi lasciarne vuote al massimo 2 su 6.
                </p>
                {[
                  ["Come valuti il rapporto con le insegnanti?", "🤩"],
                  ["Come valuti l'organizzazione?", "😊"],
                  ["Come valuti la comunicazione con i genitori?", "🙂"],
                ].map(([label, emoji]) => (
                  <div key={label} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <span className="font-ui text-sm text-[#1A362D]">{label}</span>
                    <span className="inline-flex items-center gap-1.5 sm:gap-2 text-2xl">
                      <span className="opacity-30 grayscale">😞</span>
                      <span className="opacity-30 grayscale">😐</span>
                      <span className="opacity-30 grayscale">🙂</span>
                      <span className="opacity-30 grayscale">😊</span>
                      <span className="opacity-30 grayscale">🤩</span>
                      <span className="ml-2 text-base font-ui text-[#8A9691]">→ {emoji}</span>
                    </span>
                  </div>
                ))}
                <p className="font-ui text-xs italic text-[#8A9691]">
                  …e altre tre valutazioni facoltative (Spazi, Mensa, Ascolto).
                </p>
              </div>

              {/* Domanda 3 — radio */}
              <div className="pt-4 border-t border-[#E8E6E1]">
                <p className="font-ui text-[15px] text-[#1A362D] font-medium">
                  Se potessi tornare indietro, sceglieresti di nuovo questa scuola?
                </p>
                <div className="mt-3 flex gap-4 font-ui text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-[#C05C45] bg-[#C05C45]" />
                    <span className="text-[#1A362D]">Sì</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[#8A9691]">
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-[#8A9691]" /> No
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[#8A9691]">
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-[#8A9691]" /> Non so
                  </span>
                </div>
              </div>

              {/* Domanda 4 — testo aperto */}
              <div className="pt-4 border-t border-[#E8E6E1]">
                <p className="font-ui text-[15px] text-[#1A362D] font-medium">
                  Qual è la cosa che vorresti far sapere a un genitore che sta pensando di scegliere questa scuola?
                </p>
                <p className="mt-1 font-ui text-xs text-[#8A9691]">
                  Anche una sola frase è sufficiente — è la parte che gli altri genitori leggeranno per prima.
                </p>
                <div className="mt-3 rounded-md border border-[#E8E6E1] bg-[#FAF9F6] px-3 py-3 font-ui text-sm text-[#8A9691] italic">
                  «Scrivi con parole tue, senza pensarci troppo…»
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              <Link
                to="/cerca"
                onClick={() => trackEvent("racconta_preview_cta", { position: "after_form" })}
                className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3"
                data-testid="racconta-cta-after-preview"
              >
                Trova la tua scuola <ArrowRight size={18} />
              </Link>
              <span className="inline-flex items-center gap-1.5 font-ui text-sm text-[#5C6B64]">
                <Award size={16} className="text-[#C05C45]" />
                Sblocchi il badge <strong className="text-[#1A362D]">Pioniere</strong>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= CROSS-SELL PRODOTTI ============================= */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-4xl mx-auto rounded-2xl bg-white border border-[#E8E6E1] p-8 sm:p-12">
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 rounded-full bg-[#F2EFE8] text-[#C05C45] inline-flex items-center justify-center flex-shrink-0">
              <BookOpen size={22} />
            </span>
            <div>
              <p className="label-eyebrow text-[#C05C45]">Non hai una scuola da raccontare?</p>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl text-[#1A362D] tracking-tight leading-snug">
                Consiglia un libro, un gioco o un'attività che ha fatto la differenza.
              </h2>
              <p className="mt-4 font-ui text-base text-[#3C4A44] leading-relaxed">
                Su Per manina non ci sono solo esperienze sulle scuole: c'è anche una selezione di
                <strong> libri, giochi e materiali educativi</strong> consigliati dai genitori. Se un
                gioco è stato una svolta per tuo figlio, aiuta a farlo conoscere anche agli altri.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/prodotti"
                  onClick={() => trackEvent("racconta_products_cta", { position: "cross_sell" })}
                  className="btn-primary inline-flex items-center gap-2 text-sm px-5 py-2.5"
                  data-testid="racconta-products-cta"
                >
                  Scopri libri e giochi <ArrowRight size={16} />
                </Link>
                <span className="inline-flex items-center gap-1.5 font-ui text-xs text-[#8A9691]">
                  <Award size={14} className="text-[#C05C45]" /> Sblocchi il badge Guida
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================= CTA FINALE ============================= */}
      <section className="bg-[#1A362D] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
            Pronto? Trova la tua scuola<br />e racconta la tua esperienza.
          </h2>
          <p className="mt-4 font-ui text-base sm:text-lg text-white/80 max-w-xl mx-auto leading-relaxed">
            Bastano due minuti. Aiuti i genitori del tuo quartiere. Sblocchi i badge <strong>Pioniere</strong> e <strong>Guida</strong>.
          </p>
          <form
            onSubmit={submitSearch}
            className="mt-8 max-w-xl mx-auto bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)] p-3 flex flex-col sm:flex-row items-stretch gap-2"
            data-testid="racconta-final-search-form"
          >
            <div className="flex-1 flex items-center bg-[#FAF9F6] border border-[#E8E6E1] rounded-xl px-4 py-3">
              <Search size={20} className="text-[#8A9691] mr-2 shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nome della scuola, città o CAP…"
                className="flex-1 bg-transparent border-0 outline-none font-ui text-base text-[#1A362D] placeholder:text-[#8A9691]"
                data-testid="racconta-final-search-input"
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              data-testid="racconta-final-search-submit"
              className="btn-primary text-base px-6 py-3 whitespace-nowrap"
            >
              Trova la tua scuola
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
